import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { SonarGrid } from "@/components/ui/sonar-grid";

export default function Login() {
  const { login, register, verify2fa } = useAuth();
  const { theme } = useTheme();
  const brandLogo = theme === "dark" ? "/Logo_Full_White.webp" : "/Logo_Full_Black.webp";
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", org_name: "" });
  const [twofa, setTwofa] = useState(null);
  const [code, setCode] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const res = await login(form.email, form.password);
        if (res?.two_factor_required) { setTwofa({ challenge: res.challenge }); return; }
        navigate(res?.user?.prefs?.default_page || "/app");
      } else {
        const data = await register(form);
        navigate(data.org?.onboarding_done ? "/app" : "/onboarding");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await verify2fa(twofa.challenge, code.trim());
      navigate(data?.user?.prefs?.default_page || "/app");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SonarGrid
      gradient={["#FF6A00", "#FFD500"]}
      className="flex min-h-screen items-center justify-center bg-background px-4"
    >
      <div className="relative z-10 grid w-full max-w-5xl overflow-hidden rounded-3xl border border-border bg-card shadow-2xl md:grid-cols-2">
        <div className="relative hidden flex-col justify-between bg-gradient-to-br from-[#FF6A00] to-[#FFD500] p-10 md:flex">
          <img src="/Logo_Mono_Black.webp" alt="HalgarOS" data-testid="login-brand-logo" className="h-10 w-auto self-start" />
          <div>
            <h1 className="font-display text-4xl font-bold leading-tight text-black">
              L'événementiel est un métier de personnes.
            </h1>
            <p className="mt-4 max-w-sm text-black/70">
              Devis, contrats, planning, paiements — tout votre quotidien de prestataire, réuni dans un seul outil chaleureux.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium text-black/70">
            <Sparkles className="h-4 w-4" /> Essai gratuit 14 jours — sans carte bancaire
          </div>
        </div>

        <div className="p-8 md:p-12">
          <img src={brandLogo} alt="HalgarOS" className="mb-6 h-9 w-auto md:hidden" />
          <h2 className="font-display text-2xl font-bold text-foreground">
            {twofa ? "Vérification en deux étapes" : mode === "login" ? "Bon retour parmi nous" : "Créez votre organisation"}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {twofa ? "Saisissez le code de votre application d'authentification." : mode === "login" ? "Connectez-vous à votre espace HalgarOS" : "Quelques secondes et c'est parti."}
          </p>

          {twofa ? (
            <form onSubmit={verifyCode} className="mt-8 space-y-4" data-testid="twofa-form">
              <div>
                <Label className="text-muted-foreground">Code à 6 chiffres (ou code de secours)</Label>
                <Input data-testid="twofa-code" autoFocus className="mt-1.5 border-border bg-muted tracking-widest" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" required />
              </div>
              <Button data-testid="twofa-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#FF6A00] py-6 font-semibold text-white hover:bg-[#E65F00]">
                {loading ? "…" : "Vérifier"}
              </Button>
              <button type="button" data-testid="twofa-cancel" onClick={() => { setTwofa(null); setCode(""); }} className="w-full text-center text-sm text-muted-foreground hover:text-foreground">Retour à la connexion</button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <Label className="text-muted-foreground">Votre nom</Label>
                  <Input data-testid="reg-name" className="mt-1.5 border-border bg-muted" value={form.name} onChange={upd("name")} required />
                </div>
                <div>
                  <Label className="text-muted-foreground">Nom de l'organisation</Label>
                  <Input data-testid="reg-org" className="mt-1.5 border-border bg-muted" value={form.org_name} onChange={upd("org_name")} required placeholder="Ex: Studio Solaris" />
                </div>
              </>
            )}
            <div>
              <Label className="text-muted-foreground">Email</Label>
              <Input data-testid="login-email" type="email" className="mt-1.5 border-border bg-muted" value={form.email} onChange={upd("email")} required />
            </div>
            <div>
              <Label className="text-muted-foreground">Mot de passe</Label>
              <Input data-testid="login-password" type="password" className="mt-1.5 border-border bg-muted" value={form.password} onChange={upd("password")} required />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full rounded-full bg-[#FF6A00] py-6 font-semibold text-white hover:bg-[#E65F00]">
              {loading ? "…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
            </Button>
          </form>
          )}

          {!twofa && (
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
            <button data-testid="toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")} className="font-semibold text-[#FF6A00] hover:underline">
              {mode === "login" ? "Créer une organisation" : "Se connecter"}
            </button>
          </p>
          )}
        </div>
      </div>
    </SonarGrid>
  );
}
